/**
 * `/email-templates/:kind/edit` — edit the per-studio HTML override for one
 * transactional email template.
 *
 * Seeds the editor with the current stored override (via `read_email_template`)
 * or, when none exists, a tokenised default from the code renderer (via
 * `GET /api/email-templates/:kind/seed`). Save dispatches `edit_email_template`;
 * "Reset to default" clears the override (`reset:true`) so the code template
 * takes over again. "Preview" fills the `{{ tokens }}` with sample values and
 * renders the result in the same sandboxed iframe as View.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { dispatchAction } from "../lib/dispatch";
import { EmailPreviewModal } from "../components/EmailPreviewModal";

interface SeedResponse {
  readonly kind: string;
  readonly html_body: string;
  readonly text_body: string;
}

interface ReadOverrideResponse {
  readonly kind: string;
  readonly override: { readonly html_body: string; readonly text_body: string | null } | null;
}

// Sample values for the Preview button — replace {{ tokens }} so the operator
// sees a realistic render of THEIR edited HTML (not the raw placeholders).
const SAMPLE_TOKENS: Record<string, string> = {
  "member.display_name": "Charlotte",
  "member.first_name": "Charlotte",
  "studio.display_name": "E-Pulsive Belgravia",
  "flow.discount_code": "WELCOME20",
  "flow.opt_out_url": "https://e-pulsive.co.uk/unsubscribe/sample",
  "trigger.starts_at": "Thursday 17 July, 6:00pm",
  "trigger.trainer_display_name": "Marina",
  "trigger.new_trainer_display_name": "Eladio",
  "trigger.old_trainer_display_name": "Marina",
  "trigger.slot_weekday": "Thu",
  "trigger.slot_day": "17",
  "trigger.slot_month": "July",
  "trigger.slot_time": "6:00pm",
  "trigger.slot_location": "9 Kinnerton Street, Belgravia",
  "trigger.location": "9 Kinnerton Street, Belgravia",
  "trigger.new_location": "9 Kinnerton Street, Belgravia",
  "trigger.duration_label": "20 minutes",
  "trigger.slot_starts_at_local": "Thu, 17 Jul 2026, 18:00 BST",
  "trigger.starts_at_local": "Thu, 17 Jul 2026, 18:00 BST",
  "trigger.new_starts_at_local": "Fri, 18 Jul 2026, 18:00 BST",
  "trigger.old_starts_at_local": "Thu, 17 Jul 2026, 18:00 BST",
  "trigger.credit_line": "We've added your session credit back to your account.",
  "trigger.package_name": "10-session pack",
  "trigger.sessions_total": "10",
  "trigger.amount_str": "£1,150.00",
  "trigger.membership_tier": "Unlimited",
  "trigger.invoice_href": "https://invoice.stripe.com/i/sample",
};

function previewFill(html: string): string {
  return html.replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (m, k) => SAMPLE_TOKENS[k] ?? m);
}

export function EmailTemplateEdit(): JSX.Element {
  const { kind = "" } = useParams<{ kind: string }>();
  const navigate = useNavigate();
  const [html, setHtml] = useState<string>("");
  const [text, setText] = useState<string>("");
  const [hasOverride, setHasOverride] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const [seed, read] = await Promise.all([
          api.get<SeedResponse>(`/api/email-templates/${kind}/seed`),
          dispatchAction<{ kind: string }, ReadOverrideResponse>(
            "read_email_template",
            { kind },
          ),
        ]);
        if (cancelled) return;
        const override = read.ok ? read.response.override : null;
        setHasOverride(override !== null);
        setHtml(override?.html_body ?? seed.html_body);
        setText(override?.text_body ?? seed.text_body ?? "");
        setError(null);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? `load_failed:${e.status}` : (e as Error).message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind]);

  const onSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      const r = await dispatchAction<
        { kind: string; html_body: string; text_body?: string },
        { written: boolean; cleared: boolean }
      >("edit_email_template", {
        kind,
        html_body: html,
        ...(text.length > 0 ? { text_body: text } : {}),
      });
      if (!r.ok) {
        setError(`save_failed (${r.code ?? "unknown"}): ${r.message ?? ""}`);
      } else {
        setHasOverride(true);
        setStatus("Saved. Live on the next send.");
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }, [kind, html, text]);

  const onReset = useCallback(async () => {
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      const r = await dispatchAction<
        { kind: string; reset: boolean },
        { cleared: boolean }
      >("edit_email_template", { kind, reset: true });
      if (!r.ok) {
        setError(`reset_failed (${r.code ?? "unknown"}): ${r.message ?? ""}`);
      } else {
        setHasOverride(false);
        setStatus("Reverted to the code default. Reloading the default…");
        const seed = await api.get<SeedResponse>(`/api/email-templates/${kind}/seed`);
        setHtml(seed.html_body);
        setText(seed.text_body);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }, [kind]);

  const previewHtml = useMemo(() => previewFill(html), [html]);

  return (
    <div>
      <h1 className="page-title">
        Edit email · <code>{kind}</code>
      </h1>
      <p className="dc-muted" style={{ maxWidth: "680px" }}>
        {hasOverride
          ? "This template has a saved override. Edit the HTML below, or reset to the built-in default."
          : "No override yet — you're editing a copy of the built-in default. Saving creates a per-studio override."}{" "}
        Use tokens like <code>{"{{ member.display_name }}"}</code> and{" "}
        <code>{"{{ trigger.trainer_display_name }}"}</code>; they're filled in per member at send time.
      </p>

      {loading ? <p className="dc-muted">Loading…</p> : null}
      {error !== null ? <p className="action-modal-error">{error}</p> : null}
      {status !== null ? <p className="dc-muted">{status}</p> : null}

      {!loading ? (
        <>
          <div className="form-row">
            <label>HTML body</label>
            <textarea
              value={html}
              rows={20}
              spellCheck={false}
              style={{ fontFamily: "ui-monospace, monospace", fontSize: "12.5px" }}
              onChange={(e) => setHtml(e.target.value)}
            />
          </div>
          <div className="form-row">
            <label>Plaintext twin (optional)</label>
            <textarea
              value={text}
              rows={6}
              spellCheck={false}
              style={{ fontFamily: "ui-monospace, monospace", fontSize: "12.5px" }}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <div className="modal-actions" style={{ gap: "10px", display: "flex" }}>
            <button type="button" className="row-action-btn" disabled={saving} onClick={() => void onSave()}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" className="row-action-btn" onClick={() => setShowPreview(true)}>
              Preview
            </button>
            {hasOverride ? (
              <button type="button" className="row-action-btn" disabled={saving} onClick={() => void onReset()}>
                Reset to default
              </button>
            ) : null}
            <button type="button" className="row-edit-modal-cancel" onClick={() => navigate("/email-templates")}>
              Back
            </button>
          </div>
        </>
      ) : null}

      {showPreview ? (
        <EmailPreviewModal
          title={`Preview · ${kind}`}
          subtitle="Your edited HTML with sample values filled in."
          html={previewHtml}
          onClose={() => setShowPreview(false)}
        />
      ) : null}
    </div>
  );
}
